import { Button, Heading, NavigationModal } from '@lmc/ui-kit'
import { Divider } from 'antd'
import { showToast } from 'common/utils'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ModalLayout from 'screens/Rostering/components/ModalLayout'
import { SHIFT, VISIT } from 'screens/Rostering/constants'
import useAllocateCarer from 'screens/Rostering/hooks/useAllocateCarer'
import useAllocationDoubleBookings from 'screens/Rostering/hooks/useAllocationDoubleBookings'
import { RotaInstanceStatus } from 'screens/Rostering/types'
import { styled } from 'styled-components'
import AllocationDetailedInformation from './components/AllocationDetailedInformation'
import AllocationTable from './components/AllocationTable'
import DoubleBookedModal from './components/DoubleBookedModal'

type AllocationWindowProps = {
  allocationWindowVisible: boolean
  onCancel: () => void
  revisionDetail: any
}

const AllocationWindow = ({
  allocationWindowVisible,
  onCancel,
  revisionDetail
}: AllocationWindowProps) => {
  const [addedIds, setAddedIds] = useState<string[]>([])
  const [removedIds, setRemovedIds] = useState<string[]>([])

  const [showPrompt, setShowPrompt] = useState(false)
  const [showDoubleBookedPrompt, setShowDoubleBookedPrompt] = useState(false)

  const [doubleBook, setDoubleBook] = useState(false)

  const { allocateCarer, isAllocateLoading } = useAllocateCarer({
    id: revisionDetail?.id,
    onSuccess: () => {
      resetIds()
      onCancel()
      showToast({ title: t('SUCCESSFULLY_SAVED') })
    }
  })

  const { doubleBookings } = useAllocationDoubleBookings()

  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: ['rostering_editing_limitations']
  })?.data

  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled

  const isAllocationEditable = !(
    flagsRosteringEditingLimitations &&
    (revisionDetail?.status === RotaInstanceStatus.DONE ||
      revisionDetail?.status === RotaInstanceStatus.CANCELLED)
  )

  const resetIds = () => {
    setAddedIds([])
    setRemovedIds([])
  }

  const handleCancel = () => {
    if (addedIds.length > 0 || removedIds.length > 0) {
      setShowPrompt(true)
      return
    }
    resetIds()
    onCancel()
  }

  const handleSave = async () => {
    if (addedIds.length > 0 || removedIds.length > 0) {
      const teamMembersDoubleBooked = await doubleBookings({
        id: revisionDetail?.id,
        teamMembers: addedIds
      })

      if (!showDoubleBookedPrompt && teamMembersDoubleBooked?.length > 0) {
        setShowDoubleBookedPrompt(true)
        return
      }

      allocateCarer({
        add: addedIds.map((id) => ({
          teamMemberId: id,
          role: 'carer'
        })),
        remove: removedIds,
        doubleBooked: teamMembersDoubleBooked,
        allowDoubleBooking: doubleBook
      })

      setShowDoubleBookedPrompt(false)
    } else {
      onCancel()
    }
  }

  const confirmNavigationDoubleBooked = () => {
    handleSave()
    setShowPrompt(false)
    resetIds()
    onCancel()
  }

  const confirmNavigationModal = () => {
    setShowPrompt(false)
    resetIds()
    onCancel()
  }

  return (
    <ModalLayout
      open={allocationWindowVisible}
      onCancel={handleCancel}
      headerHeight={7}
      headerContent={
        <>
          <StyledHeading level={2}>{t('ALLOCATE_TEAM_MEMBERS')}</StyledHeading>
          <StyledDivider />
        </>
      }
      footerContent={
        <FooterWrapper>
          <FooterRightSide>
            <Button onClick={handleCancel}>{t('CANCEL')}</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isAllocateLoading}
            >
              {t('SAVE')}
            </Button>
          </FooterRightSide>
        </FooterWrapper>
      }
    >
      <ModalContainer>
        <AllocationDetailedInformation revisionDetail={revisionDetail} />
        <AllocationTable
          revisionId={revisionDetail?.id}
          type={revisionDetail?.siteShift ? SHIFT : VISIT}
          addedIds={addedIds}
          setAddedIds={setAddedIds}
          removedIds={removedIds}
          setRemovedIds={setRemovedIds}
          isAllocationEditable={isAllocationEditable}
        />
      </ModalContainer>
      <DoubleBookedModal
        showPrompt={showDoubleBookedPrompt}
        setShowPrompt={setShowDoubleBookedPrompt}
        confirmNavigation={confirmNavigationDoubleBooked}
        isDoubleBookedSelected={doubleBook}
        setIsDoubleBookedSelected={setDoubleBook}
        cancelNavigation={() => {
          setShowDoubleBookedPrompt(false)
          setDoubleBook(false)
          resetIds()
        }}
      />
      <NavigationModal
        showPrompt={showPrompt}
        modalTitle={t('ARE_YOU_SURE')}
        modalContentText={t('EXIT_ALLOCATION')}
        modalConfirmText={t('EXIT')}
        modalCancelText={t('CANCEL')}
        confirmNavigation={confirmNavigationModal}
        cancelNavigation={() => setShowPrompt(false)}
      />
    </ModalLayout>
  )
}
const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  font-size: 24px;
`
const StyledDivider = styled(Divider)`
  margin: 16px 0;
`
const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`
const FooterRightSide = styled.div`
  display: flex;
  gap: 8px;
`
const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: ${(props) => props.theme.color.neutral.color3};
`

export default AllocationWindow
